import { disallowWordCharacters } from '@src/common';
import { LayoutColors } from '@src/core/theme';
import { BodyLargeBold, BodyRegularSemiBold, BodySmall, BodySmallMedium } from '@src/fonts/typography/Typography';
import useTranslate from '@src/i18n/useTranslate';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { device } from '@src/common/breakpoints';
import Input from '@src/components/ui/input';

import { PaymentMethodFormProps } from './types';
import { track } from '@src/services/analytics';
import { useAppSelector } from '@src/core/hooks';
import { buyerIsForeign } from '@src/features/auth';
import CardBrand from '@src/components/reusable/CardBrand';

export const CardPaymentForm = (props: PaymentMethodFormProps) => {
  const { paymentOption, handleSubmit, errors } = props;
  const { i18n } = useTranslate();

  const isForeign = useAppSelector(buyerIsForeign);

  const getError = (field: string) => {
    const valName = `paymentCard.${field}`;
    return errors[valName] ? errors[valName]!.join(', ') : null;
  };

  useEffect(() => {
    track('checkoutPaymentFormCard');
  }, []);

  return (
    <CardPayment onSubmit={handleSubmit}>
      <input type='hidden' name='paymentMethodId' value={paymentOption?.paymentMethod.id} />
      <CardPaymentTitle>{paymentOption?.paymentMethod.name}</CardPaymentTitle>
      <PaymentSoftdescriptor>
        {i18n('checkout_method_softdescriptor')} {paymentOption?.softDescriptor}
      </PaymentSoftdescriptor>
      <Input
        name='holder'
        label={i18n('checkout_nameAsInCard')}
        hint={getError('holder') ? '' : i18n('checkout_nameAsInCardHint')}
        error={getError('holder')}
      />

      {!isForeign ? (
        <TwoInputs>
          <Input
            name='holderIdentity'
            mask={['cpf', 'cnpj']}
            label={i18n('checkout_card_owner_cpf_cnpj')}
            error={getError('holderIdentity')}
          />

          <Input
            name='postalCode'
            label={i18n('checkout_postalCode')}
            mask={!isForeign ? 'br_postal_code' : undefined}
            error={getError('postalCode')}
            onKeyPress={disallowWordCharacters}
          />
        </TwoInputs>
      ) : null}

      <TwoInputs>
        <Input
          name='cardNumber'
          mask='card_number'
          label={i18n('checkout_cardNumber')}
          error={getError('cardNumber')}
          onKeyPress={disallowWordCharacters}
          hint={
            <Brands>
              {paymentOption?.brands?.map((brand) => (
                <CardBrand code={brand.code} key={brand.code} />
              ))}
            </Brands>
          }
        />
        <Input
          name='securityCode'
          mask='security_card_code'
          label={i18n('checkout_securityCode')}
          error={getError('securityCode')}
          type='text'
        />
      </TwoInputs>

      <TwoInputs>
        <SingleInput>
          <Label>{i18n('checkout_valid_date')}</Label>

          <Input
            name='cardValidity'
            placeholder='__/__'
            mask='payment_card_validity'
            error={getError('validityMonth') ?? getError('validityYear')}
          />
        </SingleInput>

        <SingleInput>
          <Label>{i18n('checkout_paymentIn')}</Label>
          <Select name='installments' defaultValue={paymentOption?.installmentOptions[0]?.division}>
            {paymentOption?.installmentOptions.map(({ division, label }) => (
              <option key={division} value={division}>
                {label}
              </option>
            ))}
          </Select>
        </SingleInput>
      </TwoInputs>
      <CompletePurchase data-cy='completePurchase'>{i18n('checkout_complete_purchase')}</CompletePurchase>
    </CardPayment>
  );
};

const CardPayment = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardPaymentTitle = styled.h4`
  ${BodyLargeBold};
  color: ${LayoutColors.justBlue};
`;

const Brands = styled.div`
  display: flex;
  margin-top: 4px;
  img {
    width: 25px;
    margin-right: 4px;
  }
`;

const Label = styled.label`
  ${BodySmall};
`;

const SingleInput = styled.div`
  width: 100%;
  color: ${LayoutColors.blackDark};
  ${BodySmall};
`;

const TwoInputs = styled.div`
  display: flex;
  align-items: flex-start;
  flex-flow: column;
  gap: 16px;

  ${device.smallTablet} {
    flex-flow: row;
    gap: 0 15px;
  }
`;

const CompletePurchase = styled.button`
  width: 100%;
  background-color: ${LayoutColors.successDark};
  height: 44px;
  ${BodyRegularSemiBold};
  border-radius: 4px;
  padding: 8px;
  margin-top: 16px;
  color: ${LayoutColors.whiteLight};
  border-style: none;
  cursor: pointer;
`;

const InputSelectStyle = css`
  width: 100%;
  margin-bottom: 4px;
  border-radius: 4px;
  background-color: ${LayoutColors.whiteLight};
  border: 1px solid ${LayoutColors.blackLight};
  padding: 12px;
  height: 46px;
`;

const Select = styled.select`
  ${InputSelectStyle};

  -webkit-appearance: none;
  -moz-appearance: none;
  background: white;
  background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='30' viewBox='0 0 24 24' width='40' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 6px;
  cursor: pointer;

  option {
    background-color: white !important;
    color: ${LayoutColors.blackMain} !important;
  }
`;

const PaymentSoftdescriptor = styled.span`
  width: 100%;
  ${BodySmallMedium};
  color: ${LayoutColors.justBlue};
  background-color: ${LayoutColors.justBlueLigth};
  padding: 8px;
  border-radius: 4px;
`;
